import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { getLocalApiOrigin } from '../localApi';

// Blender supplies tapered, branching channels and a baked plasma flipbook.
// This layer owns only presentation: no card state, targeting, or gameplay rolls.
const PROFILES = {
  bolt: { scale: 1, energy: 1, impact: 105, duration: 1050, color: 0x9bcdff },
  storm: { scale: 1.2, energy: 1.12, impact: 125, duration: 1300, color: 0xa4baff },
  sparkmage: { scale: .8, energy: .78, impact: 85, duration: 900, color: 0x8ae1ff },
};
const VERT = /* glsl */ `
  uniform float uTime, uReveal, uCorona;
  varying float vHeight;
  void main() {
    vec3 p = position + normal * uCorona;
    // A slanted channel stays legible in the nearly overhead table camera.
    p.x += position.y * .38;
    p.z -= position.y * .24;
    vHeight = clamp(p.y / 44., 0., 1.);
    float anchor = sin(vHeight * 3.14159);
    p.x += sin(p.y * 2.1 + uTime * 31.) * .085 * anchor;
    p.z += sin(p.y * 1.6 + uTime * 23.) * .065 * anchor;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.);
  }
`;
const FRAG = /* glsl */ `
  uniform vec3 uColor;
  uniform float uEnergy, uReveal;
  varying float vHeight;
  void main() {
    float leader = smoothstep(1. - uReveal - .025, 1. - uReveal + .025, vHeight);
    gl_FragColor = vec4(uColor * uEnergy, leader * min(1., uEnergy));
  }
`;
const PLANE_VERT = 'varying vec2 vUv; void main(){vUv=uv; gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.);}';
const IMPACT_FRAG = /* glsl */ `
  uniform sampler2D uAtlas;
  uniform float uFrame, uOpacity, uLoaded;
  uniform vec3 uColor;
  varying vec2 vUv;
  vec4 tile(float f) {
    vec2 at = vec2(mod(f,4.),3.-floor(f/4.));
    return texture2D(uAtlas,(clamp(vUv,.003,.997)+at)/4.);
  }
  void main() {
    vec4 tex = mix(tile(floor(uFrame)),tile(min(15.,floor(uFrame)+1.)),fract(uFrame));
    float d=length(vUv-.5);
    float glow=exp(-d*d*95.) * .25;
    vec3 color=mix(uColor,tex.rgb*2.2,uLoaded);
    float a=mix(glow,pow(tex.a,.72),uLoaded);
    gl_FragColor=vec4(color,a*uOpacity);
  }
`;
const SPARK_VERT = /* glsl */ `
  uniform float uAge;
  attribute vec3 aVelocity;
  attribute float aLife, aSize;
  varying float vAlpha;
  varying vec2 vUv;
  void main(){
    float t=max(0.,uAge);
    vec3 p=aVelocity*t; p.y-=13.*t*t;
    vec4 center=modelViewMatrix*vec4(p,1.);
    vec3 velocity=(modelViewMatrix*vec4(aVelocity-vec3(0.,26.*t,0.),0.)).xyz;
    vec2 direction=normalize(velocity.xy+vec2(.001));
    vec2 across=vec2(-direction.y,direction.x);
    center.xy+=direction*position.y*aSize*3.5+across*position.x*aSize*.28;
    vAlpha=(uAge<0.||t>aLife)?0.:pow(1.-t/aLife,1.6);
    vUv=uv; gl_Position=projectionMatrix*center;
  }
`;
const SPARK_FRAG = 'varying vec2 vUv; varying float vAlpha; void main(){float a=pow(max(0.,1.-abs(vUv.x-.5)*2.),2.); gl_FragColor=vec4(1.6,1.8,2.,a*vAlpha);}';

function seeded(seed) {
  let s = seed >>> 0;
  return () => { s = Math.imul(1664525, s) + 1013904223 | 0; return (s >>> 0) / 4294967296; };
}
function material(vertexShader, fragmentShader, uniforms) {
  return new THREE.ShaderMaterial({ vertexShader, fragmentShader, uniforms,
    transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, toneMapped: false });
}
function fallbackChannel() {
  const points = [new THREE.Vector3(5,44,-3),new THREE.Vector3(-1,31,2),new THREE.Vector3(2,23,0),new THREE.Vector3(-2,12,-1),new THREE.Vector3(0,0,0)];
  const path = new THREE.CurvePath();
  for (let i=1;i<points.length;i++) path.add(new THREE.LineCurve3(points[i-1],points[i]));
  return new THREE.TubeGeometry(path,48,.055,4,false);
}
function sparkGeometry(random) {
  const quad = new THREE.PlaneGeometry(1,1);
  const geom = new THREE.InstancedBufferGeometry();
  geom.index=quad.index; geom.attributes.position=quad.attributes.position; geom.attributes.uv=quad.attributes.uv;
  const velocities=[], lives=[], sizes=[];
  for (let i=0;i<32;i++) {
    const angle=random()*Math.PI*2, speed=4+random()*12;
    velocities.push(Math.cos(angle)*speed,3+random()*12,Math.sin(angle)*speed);
    lives.push(.2+random()*.55); sizes.push(.2+random()*.6);
  }
  geom.setAttribute('aVelocity',new THREE.InstancedBufferAttribute(new Float32Array(velocities),3));
  geom.setAttribute('aLife',new THREE.InstancedBufferAttribute(new Float32Array(lives),1));
  geom.setAttribute('aSize',new THREE.InstancedBufferAttribute(new Float32Array(sizes),1));
  geom.instanceCount=32;
  return geom;
}

export function createLightningLayer(scene, frameSource, options = {}) {
  const now = options.now || (() => performance.now());
  const reducedMotion = options.reducedMotion || (() => globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
  const fallback = fallbackChannel(), ground = new THREE.PlaneGeometry(18,18);
  let channels = [], atlas = null, disposed = false, serial = 0, assetsReady = false;
  const pool = Array.from({ length: 4 }, () => {
    const light=new THREE.PointLight(0xa6ceff,0,65,2);
    scene.add(light);
    return { light, active:false, group:null, mats:[], transient:[], startedAt:0 };
  });
  const warmMaterials = [], warmGeometry = [];
  const ready = options.loadAssets === false ? Promise.resolve() : Promise.allSettled([
    new GLTFLoader().loadAsync(`${getLocalApiOrigin()}/game-assets/lightning/channels.glb`),
    new THREE.TextureLoader().loadAsync(`${getLocalApiOrigin()}/game-assets/lightning/impact.png`),
  ]).then(async ([modelResult, textureResult]) => {
    const geometries=[];
    if(modelResult.status === 'fulfilled') {
      modelResult.value.scene.updateMatrixWorld(true);
      modelResult.value.scene.traverse((o) => { if(o.isMesh) {
        o.geometry.applyMatrix4(o.matrixWorld); geometries.push(o.geometry); o.material.dispose();
      } });
    }
    const texture = textureResult.status === 'fulfilled' ? textureResult.value : null;
    if(disposed) { geometries.forEach((g)=>g.dispose()); texture?.dispose(); return; }
    channels=geometries; atlas=texture;
    if(atlas) {
      atlas.colorSpace=THREE.SRGBColorSpace; atlas.minFilter=THREE.LinearFilter;
      atlas.magFilter=THREE.LinearFilter; atlas.generateMipmaps=false;
    }
    if(options.renderer && options.camera && channels.length && atlas) {
      // Retain these materials to keep the compiled GPU programs cached. The
      // first actual strike should not pay for three shader compilations.
      const warm = new THREE.Group();
      warmMaterials.push(
        material(VERT,FRAG,{uTime:{value:0},uReveal:{value:0},uCorona:{value:0},uEnergy:{value:0},uColor:{value:new THREE.Color(0xffffff)}}),
        material(PLANE_VERT,IMPACT_FRAG,{uAtlas:{value:atlas},uFrame:{value:0},uOpacity:{value:0},uLoaded:{value:1},uColor:{value:new THREE.Color(0xffffff)}}),
        material(SPARK_VERT,SPARK_FRAG,{uAge:{value:-1}}),
      );
      const sparks=sparkGeometry(seeded(1)); warmGeometry.push(sparks);
      [channels[0],ground,sparks].forEach((g,i)=>warm.add(new THREE.Mesh(g,warmMaterials[i])));
      options.renderer.initTexture(atlas);
      await options.renderer.compileAsync(warm,options.camera,scene);
    }
    assetsReady=channels.length === 3 && !!atlas;
    if(!assetsReady) console.warn('[lightning] incomplete assets; using available fallback');
  }).catch((error) => { if(!disposed) console.warn('[lightning] asset preload failed; using fallback',error); });

  function teardown(slot) {
    if(slot.group) scene.remove(slot.group);
    slot.mats.forEach((m)=>m.dispose()); slot.transient.forEach((g)=>g.dispose());
    slot.group=null; slot.mats=[]; slot.transient=[]; slot.light.intensity=0; slot.active=false;
  }
  function fire({ x, y=0.5, z, variant='bolt', seed, onImpact } = {}) {
    if(disposed || !Number.isFinite(x) || !Number.isFinite(z)) return;
    const slot=pool.find((s)=>!s.active) || pool.reduce((a,b)=>a.startedAt<=b.startedAt?a:b);
    teardown(slot);
    const profile=PROFILES[variant] || PROFILES.bolt;
    const random=seeded(seed ?? ++serial*7139);
    Object.assign(slot,{active:true,startedAt:now(),profile,variant,reduced:reducedMotion(),impacted:false,onImpact});
    const group=new THREE.Group(); group.name='Lightning strike'; group.position.set(x,y+.12,z);
    slot.group=group; scene.add(group); slot.light.position.set(x,y+4,z); slot.light.color.setHex(profile.color);
    const color=new THREE.Color(profile.color);
    if(!slot.reduced) {
      const geo=channels[Math.floor(random()*channels.length)] || fallback;
      const rotation=random()*Math.PI*2;
      for(const corona of [0,.11]) {
        const mat=material(VERT,FRAG,{uTime:{value:0},uReveal:{value:0},uCorona:{value:corona},uEnergy:{value:0},uColor:{value:corona?color:new THREE.Color(0xe8f7ff)}});
        const mesh=new THREE.Mesh(geo,mat); mesh.rotation.y=rotation;
        mesh.scale.set(profile.scale,profile.scale,profile.scale); mesh.frustumCulled=false;
        group.add(mesh); slot.mats.push(mat);
      }
      const geom=sparkGeometry(random);
      const mat=material(SPARK_VERT,SPARK_FRAG,{uAge:{value:-1}});
      const sparks=new THREE.Mesh(geom,mat); sparks.frustumCulled=false;
      group.add(sparks); slot.mats.push(mat); slot.transient.push(geom);
      slot.sparkMat=mat;
    }
    const impactMat=material(PLANE_VERT,IMPACT_FRAG,{uAtlas:{value:atlas},uFrame:{value:0},uOpacity:{value:0},uLoaded:{value:atlas?1:0},uColor:{value:color}});
    const impact=new THREE.Mesh(ground,impactMat); impact.rotation.x=-Math.PI/2;
    impact.rotation.z=random()*Math.PI*2; impact.scale.setScalar(profile.scale);
    impact.renderOrder=25; group.add(impact); slot.mats.push(impactMat); slot.impactMat=impactMat;
    slot.duration=slot.reduced?400:profile.duration;
  }
  function update() {
    const time=now();
    for(const slot of pool) {
      if(!slot.active) continue;
      const ms=time-slot.startedAt, p=slot.profile;
      if(ms>=slot.duration) { teardown(slot); continue; }
      const impactAt=slot.reduced?0:p.impact;
      const age=(ms-impactAt)/1000;
      if(age>=0 && !slot.impacted) {
        slot.impacted=true;
        const event={variant:slot.variant,position:slot.group.position.clone(),reduced:slot.reduced};
        try { (slot.onImpact || options.onImpact)?.(event); } catch(error) { console.warn('[lightning] impact callback failed',error); }
      }
      if(slot.reduced) {
        // One soft contact glow. No sky stroke, particles, light flash or camera motion.
        slot.impactMat.uniforms.uLoaded.value=0;
        slot.impactMat.uniforms.uOpacity.value=Math.sin(Math.PI*ms/slot.duration)*.7;
        continue;
      }
      const flash=age<0?.09:Math.exp(-age*24)+.46*Math.exp(-Math.pow((age-.15)/.028,2))+.16*Math.exp(-Math.pow((age-.29)/.038,2));
      for(const mat of slot.mats.slice(0,2)) {
        mat.uniforms.uTime.value=ms/1000;
        mat.uniforms.uReveal.value=Math.min(1,ms/p.impact);
        mat.uniforms.uEnergy.value=(mat.uniforms.uCorona.value? .22:3.6)*flash*p.energy;
      }
      slot.light.intensity=age<0?0:flash*225*p.energy;
      slot.sparkMat.uniforms.uAge.value=age;
      slot.impactMat.uniforms.uFrame.value=Math.max(0,Math.min(15,age/.8*15));
      slot.impactMat.uniforms.uOpacity.value=age<0?0:Math.max(0,1-age/.95)*1.65*p.energy;
    }
  }
  const unsubscribe=frameSource.onFrame(update);
  return {
    fire, ready,
    inspect:()=>({assetsReady,active:pool.filter((s)=>s.active).map((s)=>({variant:s.variant,reduced:s.reduced,age:now()-s.startedAt,impacted:s.impacted})),channels:channels.length}),
    dispose() {
      disposed=true; unsubscribe?.(); pool.forEach((s)=>{teardown(s);scene.remove(s.light);});
      channels.forEach((g)=>g.dispose()); atlas?.dispose(); fallback.dispose(); ground.dispose();
      warmMaterials.forEach((m)=>m.dispose()); warmGeometry.forEach((g)=>g.dispose());
    },
  };
}
